import { useEffect, useRef } from "react";

export default function useTableAutoScroll() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const cell = target.closest("th, td") as HTMLElement;
      if (!cell) return;

      const wrapper =
        (container.querySelector('[data-slot="wrapper"]') as HTMLElement) ??
        (container.querySelector("table")?.parentElement as HTMLElement);
      if (!wrapper) return;

      requestAnimationFrame(() => {
        const cellRect = cell.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();

        const row = cell.parentElement;
        if (!row) return;

        const cells = Array.from(row.children) as HTMLElement[];
        const cellIndex = cells.indexOf(cell);

        const firstCellWidth =
          cells.length > 0 ? cells[0].getBoundingClientRect().width : 0;
        const lastCellWidth =
          cells.length > 0
            ? cells[cells.length - 1].getBoundingClientRect().width
            : 0;
        const secondLastCellWidth =
          cells.length > 1
            ? cells[cells.length - 2].getBoundingClientRect().width
            : 0;

        // Skip auto-scroll for the sticky columns themselves
        if (
          cellIndex === 0 ||
          cellIndex === cells.length - 1 ||
          cellIndex === cells.length - 2
        ) {
          return;
        }

        const visibleLeft = wrapperRect.left + firstCellWidth;
        const visibleRight =
          wrapperRect.right - lastCellWidth - secondLastCellWidth;

        if (cellRect.left < visibleLeft) {
          wrapper.scrollLeft -= visibleLeft - cellRect.left + 16;
        } else if (cellRect.right > visibleRight) {
          wrapper.scrollLeft += cellRect.right - visibleRight + 16;
        }

        if (cellRect.top < wrapperRect.top) {
          wrapper.scrollTop -= wrapperRect.top - cellRect.top + 16;
        } else if (cellRect.bottom > wrapperRect.bottom) {
          wrapper.scrollTop += cellRect.bottom - wrapperRect.bottom + 16;
        }
      });
    };

    container.addEventListener("focusin", handleFocusIn);
    return () => container.removeEventListener("focusin", handleFocusIn);
  }, []);

  return containerRef;
}
